import { createContext, FC, ReactNode, useState } from 'react'
import { CookieKeys, CookieService } from '@/services/cookie'
import { AnalyticsService } from '@/services/analytics'
import dayjs from 'dayjs'

interface AuthContextType {
  isAuthorized: boolean
  registrantId: number | null
  authorize: (token: string, registrantId: number | null) => void
  logout: () => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
  initialIsAuthorized?: boolean
  initialRegistrantId?: number | null
}

export const AuthProvider: FC<AuthProviderProps> = ({ children, initialIsAuthorized, initialRegistrantId = null }) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean>(() => {
    if (initialIsAuthorized !== undefined) {
      return initialIsAuthorized
    }

    if (typeof window === 'undefined') {
      return false
    }

    return !!CookieService.getItem(CookieKeys.RegistrantToken)
  })
  const [registrantId, setRegistrantId] = useState<number | null>(initialRegistrantId)

  const authorize = (token: string, registrantId: number | null) => {
    // Flush pending analytics under the OLD token before it changes (CW2-648
    // §7.1): registrant_id is stamped server-side from the token at receive
    // time, so queued events must never flush under another registrant's token
    AnalyticsService.flush()

    const today = dayjs()
    const year = today.year()
    const endOfJune = dayjs(`${year}-06-30 23:59:59`) // 30 June 23:59:59
    const endOfDecember = dayjs(`${year}-12-31 23:59:59`) // 31 December 23:59:59
    const expires = today.isBefore(endOfJune) ? endOfJune : endOfDecember

    CookieService.setItem(CookieKeys.RegistrantToken, token, {
      expires: expires.toDate(),
    })

    setIsAuthorized(true)
    setRegistrantId(registrantId)
  }

  const logout = () => {
    AnalyticsService.flush()
    CookieService.removeItem(CookieKeys.RegistrantToken)
    setIsAuthorized(false)
    setRegistrantId(null)
  }

  return (
    <AuthContext.Provider value={{ isAuthorized, authorize, logout, registrantId }}>{children}</AuthContext.Provider>
  )
}
